import { InlineStack, Select, TextLabel } from '@customerio/pluma-components/react';
import type { DeliveryFilterValues } from '../types';
import { buildChannelSelectOptions } from '../utils/state-options';
import { DeliveryDateRange } from './delivery-date-range';
import { StatusFilter } from './status-filter';

type PrimaryFilterRowProps = {
  values: DeliveryFilterValues;
  channels: readonly string[];
  isDisabled: boolean;
  searchByEmail: boolean;
  withStatusFilter: boolean;
  withDateRange: boolean;
  canClearTimeRange: boolean;
  maxRange: string | number;
  dateRangeTooltipMessage?: string;
  onChannelChange: (value: string | null) => void;
  onStatusChange: (value: string | null) => void;
  onDateChange: (range: { startTs: number | null; endTs: number | null }) => void;
};

/**
 * The always-visible top row of the filter bar: the channel select, the grouped
 * status select, and the date-range picker. Split out of `DeliveryFilters` so
 * each component stays a shallow, low-complexity composition.
 */
export function PrimaryFilterRow({
  values,
  channels,
  isDisabled,
  searchByEmail,
  withStatusFilter,
  withDateRange,
  canClearTimeRange,
  maxRange,
  dateRangeTooltipMessage,
  onChannelChange,
  onStatusChange,
  onDateChange,
}: PrimaryFilterRowProps) {
  const channelOptions = buildChannelSelectOptions(channels);

  return (
    <InlineStack alignBlock="center" gap="125">
      <TextLabel>Filter by</TextLabel>

      {channelOptions.length > 1 ? (
        <Select
          ariaLabel="Filter by channel"
          value={values.actionType}
          options={channelOptions}
          onChange={onChannelChange}
          isDisabled={isDisabled}
          size="sm"
        />
      ) : null}

      {withStatusFilter ? (
        <StatusFilter
          value={values.deliveryStatus}
          channels={channels}
          actionType={values.actionType}
          isDisabled={isDisabled || searchByEmail}
          searchByEmail={searchByEmail}
          onChange={onStatusChange}
        />
      ) : null}

      {withDateRange ? (
        <DeliveryDateRange
          startTs={values.startTs}
          endTs={values.endTs}
          onChange={onDateChange}
          isDisabled={isDisabled}
          isClearable={canClearTimeRange}
          maxRange={maxRange}
          tooltipMessage={dateRangeTooltipMessage}
        />
      ) : null}
    </InlineStack>
  );
}
